import { useTranslation } from "react-i18next";
import { Activity, Check, ClipboardCheck, Pill, Repeat, RotateCcw, Utensils, type LucideIcon } from "lucide-react";
import { Avatar, Button } from "@akuna/ui";
import type { ActivityRow } from "./useStayActivities";

const GRID = "grid-cols-[60px_1.4fr_120px_1.6fr_130px_140px]";

// Ícone por nome vindo do DB (data-driven, sem dependência de lucide/dynamic).
const TYPE_ICONS: Record<string, LucideIcon> = {
  pill: Pill,
  utensils: Utensils,
  activity: Activity,
  "clipboard-check": ClipboardCheck,
};

const badgeStyle = (color: string) => ({
  color,
  background: `color-mix(in srgb, ${color} 14%, transparent)`,
});

function TargetCell({ row }: { row: ActivityRow }) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <Avatar
        className="!h-[34px] !w-[34px] shrink-0 text-xs"
        style={row.targetColor ? badgeStyle(row.targetColor) : undefined}
        name={row.targetLabel}
        fallback={row.petInitial ?? row.targetLabel.slice(0, 1)}
      />
      <div className="min-w-0">
        <div className="truncate font-display text-[13.5px] font-bold leading-tight text-foreground">{row.targetLabel}</div>
        {row.targetSub && <div className="text-[11px] text-muted-foreground">{row.targetSub}</div>}
      </div>
    </div>
  );
}

function TypeBadge({ row }: { row: ActivityRow }) {
  const Icon = TYPE_ICONS[row.typeIcon] ?? Activity;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold" style={badgeStyle(row.typeColor)}>
      <Icon size={14} />
      {row.typeName}
    </span>
  );
}

function DescCell({ row }: { row: ActivityRow }) {
  return (
    <div className="min-w-0">
      <div className="text-[13.5px] text-foreground/80">{row.description}</div>
      {row.recurrenceLabel && (
        <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11px] font-semibold" style={{ color: "#7e3690", background: "#f3e4f6" }}>
          <Repeat size={13} /> {row.recurrenceLabel}
        </div>
      )}
    </div>
  );
}

function AssigneeCell({ row }: { row: ActivityRow }) {
  if (!row.assigneeName) return <span className="text-[13px] text-muted-foreground">—</span>;
  return (
    <div className="flex items-center gap-2">
      <Avatar className="h-6 w-6 shrink-0 text-[10px]" name={row.assigneeName} fallback={row.assigneeInitial ?? "?"} />
      <span className="text-[13px] text-foreground/80">{row.assigneeName}</span>
    </div>
  );
}

function ActionCell({ row, onToggle, pending }: { row: ActivityRow; onToggle: (done: boolean) => void; pending: boolean }) {
  const { t } = useTranslation("daycareHotel");
  if (row.done) {
    return (
      <div className="flex items-center justify-end gap-3">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-success/15 px-3 py-1.5 text-[13px] font-semibold text-success">
          <Check size={16} /> {t("activities.completed")}
        </span>
        <div className="h-4 w-px bg-border" />
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("activities.undo")}
          title={t("activities.undo")}
          disabled={pending}
          onClick={() => onToggle(false)}
        >
          <RotateCcw size={16} />
        </Button>
      </div>
    );
  }
  return (
    <div className="flex justify-end">
      <Button size="sm" disabled={pending} loading={pending} onClick={() => onToggle(true)}>{t("activities.complete")}</Button>
    </div>
  );
}

interface ActivityRowViewProps {
  row: ActivityRow;
  onToggle: (done: boolean) => void;
  pending: boolean;
  onOpen: () => void;
}

const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

export function ActivityTableRow({ row, onToggle, pending, onOpen }: ActivityRowViewProps) {
  return (
    <div
      className={`grid ${GRID} cursor-pointer items-center gap-3 border-t border-border px-5 py-3.5 transition-colors hover:bg-muted/30 ${row.done ? "opacity-50" : ""}`}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
    >
      <div className="font-display text-sm font-bold text-foreground/70">{row.time}</div>
      <TargetCell row={row} />
      <div><TypeBadge row={row} /></div>
      <DescCell row={row} />
      <AssigneeCell row={row} />
      <div onClick={stopPropagation}><ActionCell row={row} onToggle={onToggle} pending={pending} /></div>
    </div>
  );
}

export function ActivityCard({ row, onToggle, pending, onOpen }: ActivityRowViewProps) {
  return (
    <div
      className={`grid cursor-pointer gap-3 border-t border-border p-4 transition-colors hover:bg-muted/30 ${row.done ? "opacity-60" : ""}`}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
    >
      <div className="flex items-center justify-between gap-3">
        <TargetCell row={row} />
        <span className="font-display text-sm font-bold text-foreground/70">{row.time}</span>
      </div>
      <div className="flex items-center gap-2"><TypeBadge row={row} /></div>
      <DescCell row={row} />
      <div className="flex items-center justify-between gap-3">
        <AssigneeCell row={row} />
      </div>
      <div onClick={stopPropagation}><ActionCell row={row} onToggle={onToggle} pending={pending} /></div>
    </div>
  );
}
